import React, { useEffect, useMemo, useState } from 'react';
import { useLocaleState, useNotify, useTranslate } from 'react-admin';
import {
  Alert, Box, Button, Card, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Stack,
  TableBody, TableCell, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography,
  useMediaQuery, useTheme,
} from '@mui/material';
import { ChevronLeftRounded, ChevronRightRounded } from '@mui/icons-material';
import dayjs from 'dayjs';
import { httpClient } from '@/utils/httpClient';
import {
  BlockHeading, DataTable, DatagridEmpty, HelpDialog, KpiTile, PageTitle, SectionHeader, StatusDot,
} from '@/components/shell';
import { useStatFormat } from '@/features/statistics/accountHealth';
import { CostCenterSelect } from './CostCenterSelect';

const PAGE_SIZE = 20;

interface CreditBalance {
  availableCredits?: number;
  reservedCredits?: number;
  isBlocked?: boolean;
}

interface CreditTransaction {
  date?: string;
  createdAt?: string;
  type?: string;
  transactionType?: string;
  amount: number;
  balanceAfter: number;
}

interface CreditEvent {
  date?: string;
  createdAt?: string;
  operationType: string;
  creditsConsumed: number;
}

interface MonthlySummaryRow {
  month: number;
  totalRequests?: number;
  totalCreditsConsumed?: number;
}

interface Page<T> {
  data?: T[];
  recordsTotal?: number;
}

const TRANSACTION_TYPES = ['TOPUP', 'REFUND', 'DEDUCT', 'MANUAL_ADJ'];
const HELP_TOPICS = ['Available', 'Reserved', 'TopUp', 'Adjustment'] as const;

const formatDate = (value?: string) => dayjs(value).format('DD/MM/YYYY HH:mm');

const pageParams = (page: number) =>
  new URLSearchParams({ draw: '1', length: String(PAGE_SIZE), start: String(page * PAGE_SIZE), search: '', column: '', orderBy: 'desc' });

/** The pagination strip of a ledger: fixed page size, so no size selector. */
const LedgerPagination: React.FC<{ count: number; page: number; onPageChange: (page: number) => void }> = ({ count, page, onPageChange }) => (
  <TablePagination
    component="div"
    count={count}
    page={page}
    onPageChange={(_, p) => onPageChange(p)}
    rowsPerPage={PAGE_SIZE}
    rowsPerPageOptions={[]}
  />
);

const CreditsHelp: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const t = useTranslate();
  return (
    <HelpDialog open={open} onClose={onClose} title={t('screens.credits.helpTitle')}>
      <Typography variant="body2">{t('screens.credits.helpIntro')}</Typography>
      {HELP_TOPICS.map((topic) => (
        <Box key={topic}>
          <Typography variant="subtitle2">{t(`screens.credits.help${topic}Title`)}</Typography>
          <Typography variant="body2" color="text.secondary">{t(`screens.credits.help${topic}`)}</Typography>
        </Box>
      ))}
    </HelpDialog>
  );
};

export const CreditsPage = () => {
  const t = useTranslate();
  const notify = useNotify();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const fmt = useStatFormat();
  const [locale] = useLocaleState();
  const [selectedCC, setSelectedCC] = useState<string>('');
  const [ccName, setCcName] = useState('');
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [txTotal, setTxTotal] = useState(0);
  const [txPage, setTxPage] = useState(0);
  const [eventLogs, setEventLogs] = useState<CreditEvent[]>([]);
  const [evTotal, setEvTotal] = useState(0);
  const [evPage, setEvPage] = useState(0);
  const [monthlySummary, setMonthlySummary] = useState<MonthlySummaryRow[]>([]);
  const [summaryYear, setSummaryYear] = useState(new Date().getFullYear());
  const [helpOpen, setHelpOpen] = useState(false);
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState('');
  const [adjAmount, setAdjAmount] = useState('');
  const [adjReason, setAdjReason] = useState('');

  const monthName = useMemo(() => {
    const format = new Intl.DateTimeFormat(locale === 'es' ? 'es-ES' : 'en-GB', { month: 'long' });
    return (month: number) => format.format(new Date(2000, month - 1, 1));
  }, [locale]);

  const loadAll = () => {
    if (!selectedCC) return;
    httpClient.get<CreditBalance>(`/admin/credit/GetBalance/${selectedCC}`).then(r => setBalance(r.data)).catch(() => {});
    loadTransactions();
    loadEventLogs();
    loadMonthlySummary();
  };

  useEffect(() => { loadAll(); }, [selectedCC]);
  useEffect(() => { if (selectedCC) loadTransactions(); }, [txPage]);
  useEffect(() => { if (selectedCC) loadEventLogs(); }, [evPage]);
  useEffect(() => { if (selectedCC) loadMonthlySummary(); }, [summaryYear]);

  const loadTransactions = async () => {
    const r = await httpClient.get<Page<CreditTransaction>>(`/admin/credit/GetTransactions/${selectedCC}?${pageParams(txPage)}`);
    setTransactions(r.data.data || []); setTxTotal(r.data.recordsTotal || 0);
  };

  const loadEventLogs = async () => {
    const r = await httpClient.get<Page<CreditEvent>>(`/admin/credit/GetEventLogs/${selectedCC}?${pageParams(evPage)}`);
    setEventLogs(r.data.data || []); setEvTotal(r.data.recordsTotal || 0);
  };

  const loadMonthlySummary = async () => {
    try {
      const r = await httpClient.get<MonthlySummaryRow[]>(`/admin/credit/GetMonthlySummary/${selectedCC}?year=${summaryYear}`);
      setMonthlySummary(r.data);
    } catch { setMonthlySummary([]); }
  };

  const handleTopUp = async () => {
    try {
      await httpClient.post('/admin/credit/TopUp', { costCenterId: selectedCC, amount: parseFloat(topUpAmount) });
      notify('screens.credits.topUpDone');
      setTopUpOpen(false); setTopUpAmount('');
      loadAll();
    } catch { notify('screens.credits.topUpFailed', { type: 'error' }); }
  };

  const handleAdjustment = async () => {
    try {
      await httpClient.post('/admin/credit/ManualAdjustment', { costCenterId: selectedCC, amount: parseFloat(adjAmount), reason: adjReason });
      notify('screens.credits.adjustmentDone');
      setAdjustOpen(false); setAdjAmount(''); setAdjReason('');
      loadAll();
    } catch { notify('screens.credits.adjustmentFailed', { type: 'error' }); }
  };

  const transactionType = (tx: CreditTransaction) => {
    const type = tx.type || tx.transactionType || '';
    return TRANSACTION_TYPES.includes(type) ? t(`screens.credits.types.${type}`) : type;
  };

  const signed = (amount: number) => `${amount >= 0 ? '+' : ''}${fmt.count(amount)}`;

  return (
    <Box>
      <SectionHeader
        left={<PageTitle title={t('menu.credits')} subtitle={t('screens.credits.subtitle')} />}
        help={{ label: t('screens.credits.help'), onClick: () => setHelpOpen(true) }}
        actions={[{ label: t('screens.credits.adjust'), onClick: () => setAdjustOpen(true), disabled: !selectedCC }]}
        create={{ label: t('screens.credits.topUp'), onClick: () => setTopUpOpen(true), disabled: !selectedCC }}
      />
      <CreditsHelp open={helpOpen} onClose={() => setHelpOpen(false)} />

      <Box sx={{ pt: 1, pb: 2 }}>
        <CostCenterSelect
          value={selectedCC}
          onChange={(id, cc) => {
            setSelectedCC(id);
            setCcName(cc?.name ?? '');
            setBalance(null);
            setTxPage(0);
            setEvPage(0);
          }}
        />
      </Box>

      {!selectedCC ? (
        <Card>
          <DatagridEmpty title={t('screens.billing.pickCostCenter')} hint={t('screens.credits.pickHint')} />
        </Card>
      ) : (
        <Stack spacing={3}>
          {balance && (
            // A Stack strips its children's margins, which Grid's negative gutter needs.
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={4}>
                  <KpiTile label={t('screens.credits.available')} value={fmt.count(balance.availableCredits ?? 0)} />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <KpiTile label={t('screens.credits.reserved')} value={fmt.count(balance.reservedCredits ?? 0)} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <KpiTile
                    label={t('screens.credits.status')}
                    textValue
                    value={t(balance.isBlocked ? 'screens.credits.blocked' : 'screens.credits.active')}
                    detail={balance.isBlocked ? <StatusDot tone="problem" label={t('screens.credits.blockedDetail')} /> : undefined}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          <Box component="section" aria-labelledby="credits-transactions">
            <BlockHeading id="credits-transactions" title={t('screens.credits.transactions')} />
            <DataTable
              label={t('screens.credits.transactions')}
              empty={transactions.length === 0 ? <DatagridEmpty title={t('screens.credits.noTransactions')} hint={t('screens.credits.noTransactionsHint')} /> : undefined}
              footer={txTotal > PAGE_SIZE ? <LedgerPagination count={txTotal} page={txPage} onPageChange={setTxPage} /> : undefined}
            >
              <TableHead>
                <TableRow>
                  <TableCell>{t('screens.credits.date')}</TableCell>
                  <TableCell>{t('screens.credits.type')}</TableCell>
                  <TableCell align="right">{t('screens.credits.amount')}</TableCell>
                  <TableCell align="right">{t('screens.credits.balanceAfter')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((tx, i) => (
                  <TableRow key={`${tx.date || tx.createdAt}-${i}`}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(tx.date || tx.createdAt)}</TableCell>
                    <TableCell>{transactionType(tx)}</TableCell>
                    <TableCell align="right">{signed(tx.amount)}</TableCell>
                    <TableCell align="right">{fmt.count(tx.balanceAfter)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </DataTable>
          </Box>

          <Box component="section" aria-labelledby="credits-monthly">
            <BlockHeading
              id="credits-monthly"
              title={t('screens.credits.monthly')}
              action={(
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Tooltip title={t('screens.credits.previousYear')}>
                    <IconButton onClick={() => setSummaryYear(y => y - 1)} aria-label={t('screens.credits.previousYear')}>
                      <ChevronLeftRounded />
                    </IconButton>
                  </Tooltip>
                  <Typography variant="body1" aria-live="polite" sx={{ fontVariantNumeric: 'tabular-nums' }}>{summaryYear}</Typography>
                  <Tooltip title={t('screens.credits.nextYear')}>
                    <IconButton onClick={() => setSummaryYear(y => y + 1)} aria-label={t('screens.credits.nextYear')}>
                      <ChevronRightRounded />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}
            />
            <DataTable
              label={t('screens.credits.monthly')}
              empty={monthlySummary.length === 0 ? <DatagridEmpty title={t('screens.credits.noMonthly', { year: summaryYear })} hint={t('screens.credits.noMonthlyHint')} /> : undefined}
            >
              <TableHead>
                <TableRow>
                  <TableCell>{t('screens.credits.month')}</TableCell>
                  <TableCell align="right">{t('screens.credits.operations')}</TableCell>
                  <TableCell align="right">{t('screens.credits.creditsConsumed')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {monthlySummary.map((row) => (
                  <TableRow key={row.month}>
                    <TableCell>{monthName(row.month)}</TableCell>
                    <TableCell align="right">{row.totalRequests != null ? fmt.count(row.totalRequests) : '—'}</TableCell>
                    <TableCell align="right">{row.totalCreditsConsumed != null ? fmt.count(row.totalCreditsConsumed) : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </DataTable>
          </Box>

          <Box component="section" aria-labelledby="credits-events">
            <BlockHeading id="credits-events" title={t('screens.credits.events')} />
            <DataTable
              label={t('screens.credits.events')}
              empty={eventLogs.length === 0 ? <DatagridEmpty title={t('screens.credits.noEvents')} hint={t('screens.credits.noEventsHint')} /> : undefined}
              footer={evTotal > PAGE_SIZE ? <LedgerPagination count={evTotal} page={evPage} onPageChange={setEvPage} /> : undefined}
            >
              <TableHead>
                <TableRow>
                  <TableCell>{t('screens.credits.date')}</TableCell>
                  <TableCell>{t('screens.credits.operationType')}</TableCell>
                  <TableCell align="right">{t('screens.credits.creditsConsumed')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {eventLogs.map((ev, i) => (
                  <TableRow key={`${ev.date || ev.createdAt}-${i}`}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(ev.date || ev.createdAt)}</TableCell>
                    <TableCell>{ev.operationType}</TableCell>
                    <TableCell align="right">{fmt.count(ev.creditsConsumed)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </DataTable>
          </Box>
        </Stack>
      )}

      <Dialog open={topUpOpen} onClose={() => setTopUpOpen(false)} maxWidth="sm" fullWidth fullScreen={isPhone}>
        <DialogTitle>{t('screens.credits.topUpTitle', { name: ccName })}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">{t('screens.credits.topUpHint')}</Typography>
            <TextField
              label={t('screens.credits.quantity')}
              type="number"
              value={topUpAmount}
              onChange={e => setTopUpAmount(e.target.value)}
              fullWidth
              inputProps={{ min: 0, step: 1 }}
              autoFocus
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setTopUpOpen(false)}>{t('ra.action.cancel')}</Button>
          <Button onClick={handleTopUp} variant="contained" disabled={!topUpAmount || parseFloat(topUpAmount) <= 0}>
            {t('screens.credits.topUpConfirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={adjustOpen} onClose={() => setAdjustOpen(false)} maxWidth="sm" fullWidth fullScreen={isPhone}>
        <DialogTitle>{t('screens.credits.adjustTitle', { name: ccName })}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info">{t('screens.credits.adjustHint')}</Alert>
            <TextField
              label={t('screens.credits.quantity')}
              type="number"
              value={adjAmount}
              onChange={e => setAdjAmount(e.target.value)}
              fullWidth
              inputProps={{ step: 1 }}
            />
            <TextField
              label={t('screens.credits.reason')}
              value={adjReason}
              onChange={e => setAdjReason(e.target.value)}
              fullWidth
              multiline
              rows={2}
              helperText={t('screens.credits.reasonHelp')}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setAdjustOpen(false)}>{t('ra.action.cancel')}</Button>
          <Button onClick={handleAdjustment} variant="contained" disabled={!adjAmount || !adjReason.trim()}>
            {t('screens.credits.adjustConfirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
